import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FileText } from 'lucide-react';
import './risk-overview.css';
import './risk-domains.css';
import iconSafety from '../../assets/domain-icons/safety.svg';
import iconLegal from '../../assets/domain-icons/legal.svg';
import iconEthics from '../../assets/domain-icons/ethics.svg';
import iconSecurity from '../../assets/domain-icons/security.svg';
import iconPerformance from '../../assets/domain-icons/performance.svg';
import iconSustainability from '../../assets/domain-icons/sustainability.svg';
import { GenerateReportButton } from '../../components/GenerateReportButton';
import { RiskPlusTopNav, RiskPlusPagerNav, RiskPlusPagerLink, riskColor, riskLabel } from './RiskPlusPageShell';
import { useRiskPlusPageData } from './useRiskPlusPageData';
import type { RiskPlusReport, RiskProfilePillar } from '../../entities/report/RiskPlusReport';
import type { TRiskRank } from '../../entities/risk/Risk';

const SEV_ORDER: TRiskRank[] = ['negligible', 'low', 'moderate', 'high', 'critical'];

const PILLAR_ICON: Record<string, string> = {
  Safety: iconSafety,
  Legal: iconLegal,
  Ethics: iconEthics,
  Security: iconSecurity,
  Performance: iconPerformance,
  Sustainability: iconSustainability,
};

function PillarIcon({ label }: { label: string }) {
  const src = PILLAR_ICON[label];
  if (!src) return <span className="aq-icon" aria-hidden>hub</span>;
  return <img className="rdom-dm-ic" src={src} alt="" />;
}

function flaggedCount(pillar: RiskProfilePillar): number {
  const hist = pillar.characteristicsHistogram;
  return (hist.moderate || 0) + (hist.high || 0) + (hist.critical || 0);
}

function RiskDomainsHeader() {
  return (
    <div className="rovw-hdr">
      <div className="rovw-hdr-icon">
        <span className="aq-icon" aria-hidden>hub</span>
      </div>
      <div className="rovw-hdr-main">
        <h2 className="rovw-hdr-title">Risk Domains</h2>
      </div>
    </div>
  );
}

function SeverityLegend() {
  return (
    <div className="rdom-legend">
      <span className="rdom-legend-title">Characteristic severity</span>
      {SEV_ORDER.map((sev) => (
        <span key={sev} className="rdom-legend-item">
          <span className="rovw-sev-dot" style={{ background: riskColor(sev) }} />
          {riskLabel(sev)}
        </span>
      ))}
    </div>
  );
}

function PillarCard({ pillar, onOpen }: { pillar: RiskProfilePillar; onOpen: () => void }) {
  const hist = pillar.characteristicsHistogram;
  const total = SEV_ORDER.reduce((sum, sev) => sum + (hist[sev] || 0), 0) || 1;
  const segments = SEV_ORDER.filter((sev) => (hist[sev] || 0) > 0).map((sev) => ({
    sev,
    width: `${(((hist[sev] || 0) / total) * 100).toFixed(2)}%`,
    title: `${hist[sev]} ${riskLabel(sev)} ${hist[sev] === 1 ? 'characteristic' : 'characteristics'}`,
  }));

  return (
    <button type="button" className="rdom-pillar-card" onClick={onOpen}>
      <div className="rdom-pillar-top">
        <div className="rdom-pillar-ic">
          <PillarIcon label={pillar.label} />
        </div>
        <div className="rdom-pillar-name">{pillar.label}</div>
        <span className="rovw-sev-chip">
          <span className="rovw-sev-dot" style={{ background: riskColor(pillar.riskRank) }} />
          {riskLabel(pillar.riskRank)}
        </span>
      </div>
      <div className="rdom-pillar-metric">
        <span className="rdom-pillar-count">{flaggedCount(pillar)}</span>
        <span className="rdom-pillar-count-lbl">characteristics flagged</span>
      </div>
      <div className="rdom-sevbar">
        {segments.map((seg) => (
          <div
            key={seg.sev}
            className="rdom-sevseg"
            style={{ width: seg.width, background: riskColor(seg.sev) }}
            title={seg.title}
            aria-label={seg.title}
          />
        ))}
      </div>
      {pillar.executiveComment && <div className="rdom-pillar-exec">{pillar.executiveComment}</div>}
      <div className="rdom-pillar-open">
        Open pillar
        <span className="aq-icon" aria-hidden>arrow_forward</span>
      </div>
    </button>
  );
}

function DeploymentCharacteristics({ report }: { report: RiskPlusReport }) {
  const characteristics = report.deploymentProfile.deploymentCharacteristics;
  if (characteristics.length === 0) return null;

  return (
    <>
      <div className="rovw-verdict-eyebrow" style={{ margin: '24px 0 12px' }}>
        Deployment characteristics
      </div>
      <div className="rovw-card">
        <div className="rdom-char-tags">
          {characteristics.map((c, i) => (
            <div key={i} className="rdom-char-tag">
              <span className="rdom-char-tag-label">{c.label}</span>
              <span className="rdom-char-tag-desc">{c.description}</span>
            </div>
          ))}
        </div>
      </div>
    </>
  );
}

function ScopeNotes({ report }: { report: RiskPlusReport }) {
  const notes = report.executiveReadinessVerdict.scopeNotes;
  if (notes.length === 0) return null;

  return (
    <div className="rovw-scope-band">
      <span className="aq-icon" aria-hidden>info</span>
      <div className="rovw-scope-band-text">{notes.join(' ')}</div>
    </div>
  );
}

function PillarDrawer({ pillar, onClose }: { pillar: RiskProfilePillar; onClose: () => void }) {
  const hist = pillar.characteristicsHistogram;
  const max = Math.max(...SEV_ORDER.map((sev) => hist[sev] || 0), 1);
  const bars = SEV_ORDER.map((sev) => ({
    sev,
    count: hist[sev] || 0,
    height: `${Math.max(((hist[sev] || 0) / max) * 100, 2)}%`,
  }));

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  return (
    <div className="rovw-scrim" onClick={onClose}>
      <div className="rovw-drawer" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
        <div className="rovw-drawer-hdr">
          <div className="rovw-drawer-hdr-top">
            <div className="rovw-drawer-hdr-ic">
              <PillarIcon label={pillar.label} />
            </div>
            <div className="rovw-drawer-title">{pillar.label}</div>
            <button type="button" className="rovw-drawer-close" onClick={onClose} aria-label="Close">
              <span className="aq-icon" aria-hidden>close</span>
            </button>
          </div>
          <div className="rdom-drawer-chips">
            <span className="rovw-sev-chip">
              <span className="rovw-sev-dot" style={{ background: riskColor(pillar.riskRank) }} />
              {riskLabel(pillar.riskRank)}
            </span>
            {pillar.histogramComment && (
              <span className="rovw-stage-pill">{pillar.histogramComment}</span>
            )}
          </div>
        </div>
        <div className="rovw-drawer-body">
          <div className="rovw-dsec">
            <div className="rovw-dsec-label">Characteristic counts by severity</div>
            <div className="rdom-multibar">
              {bars.map((bar) => (
                <div key={bar.sev} className="rdom-mb-col">
                  <div className="rdom-mb-count">{bar.count}</div>
                  <div className="rdom-mb-bar-wrap">
                    <div
                      className="rdom-mb-bar"
                      style={{ height: bar.height, background: riskColor(bar.sev) }}
                    />
                  </div>
                  <div className="rdom-mb-lbl">{riskLabel(bar.sev)}</div>
                </div>
              ))}
            </div>
          </div>
          {pillar.summary && (
            <div className="rovw-dsec">
              <div className="rovw-dsec-label">Why this pillar carries its rank</div>
              <div className="rdom-dsec-text">{pillar.summary}</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const RiskPlusRiskDomains = () => {
  const { useCaseChatId = '' } = useParams<{ useCaseChatId: string }>();
  const navigate = useNavigate();
  const {
    brief,
    riskReport,
    resolvedChat,
    useCaseChatReports,
    isLoadingBriefs,
    isLoadingReport,
    reportError,
    showReportsPanel,
    loadRiskReport,
    refreshReports,
  } = useRiskPlusPageData(useCaseChatId);

  const [openPillarLabel, setOpenPillarLabel] = useState<string | null>(null);

  if (!useCaseChatId) return null;

  if (isLoadingBriefs) {
    return (
      <div className="rovw-page min-h-screen p-6">
        <div className="mx-auto max-w-7xl animate-pulse rounded-xl bg-white p-8 shadow-sm">
          <div className="h-4 w-1/3 rounded bg-gray-200" />
          <div className="mt-4 h-8 w-2/3 rounded bg-gray-200" />
        </div>
      </div>
    );
  }

  if (!brief) {
    return (
      <div className="rovw-page min-h-screen p-6">
        <div className="mx-auto max-w-7xl rounded-xl border border-gray-200 bg-white p-8 text-center shadow-sm">
          <p className="text-midnight-navi">Use case not found.</p>
          <button
            type="button"
            onClick={() => navigate('/dashboard')}
            className="mt-4 text-sm font-medium text-royal-blue hover:text-deep-blue hover:underline"
          >
            Back to register
          </button>
        </div>
      </div>
    );
  }

  const riskPlusReportId = useCaseChatReports?.riskPlusReport?.reportId ?? null;
  const pillars = riskReport?.riskProfileSummary.pillars ?? [];
  const openPillar = pillars.find((p) => p.label === openPillarLabel) ?? null;

  return (
    <div className="rovw-page min-h-screen pb-10 pt-6">
      <div className="mx-auto max-w-[1180px] px-4 md:px-6">

        <RiskPlusTopNav
          useCaseChatId={useCaseChatId}
          secondaryNav={
            <RiskPlusPagerNav>
              <RiskPlusPagerLink
                to={`/use-case/${encodeURIComponent(useCaseChatId)}/executive`}
                label="Risk Overview"
                direction="prev"
              />
              <RiskPlusPagerLink
                to={`/use-case/${encodeURIComponent(useCaseChatId)}/risk-findings`}
                label="Risk Register"
                direction="next"
              />
            </RiskPlusPagerNav>
          }
        />

        {riskPlusReportId && isLoadingReport && !riskReport && (
          <div className="mt-4 animate-pulse space-y-3">
            <div className="h-24 rounded-xl bg-gray-100" />
            <div className="h-20 rounded-xl bg-gray-100" />
            <div className="h-48 rounded-xl bg-gray-100" />
          </div>
        )}

        {riskPlusReportId && !isLoadingReport && reportError && !riskReport && (
          <div
            className="mt-4 rounded-xl border border-red-200 bg-red-50 px-5 py-4 text-sm text-red-700 shadow-xs"
            role="alert"
          >
            <div className="font-semibold">Could not load Risk+ report</div>
            <div className="mt-1">{reportError}</div>
            <button
              type="button"
              onClick={() => void loadRiskReport()}
              className="mt-3 text-sm font-medium text-royal-blue hover:text-deep-blue hover:underline"
            >
              Retry
            </button>
          </div>
        )}

        {!riskPlusReportId && !isLoadingReport && showReportsPanel && (
          <div className="mt-6 rounded-xl border border-dashed border-gray-300 bg-white px-6 py-10 text-center shadow-xs">
            <div className="mx-auto mb-3 flex h-10 w-10 items-center justify-center rounded-full bg-gray-100">
              <FileText className="h-5 w-5 text-gray-400" />
            </div>
            <p className="text-sm font-medium text-midnight-navi">No Risk+ report generated yet</p>
            <p className="mt-1 text-xs text-gray-500">
              Generate a Risk+ report to see the risk domains, flagged characteristics, and deployment context.
            </p>
            {resolvedChat && (
              <div className="mt-4 flex justify-center">
                <GenerateReportButton
                  useCaseChat={resolvedChat}
                  reportType="risk"
                  label="Generate Risk+ Report"
                  disabled={brief.jobIsActive}
                  onGenerateReport={refreshReports}
                />
              </div>
            )}
          </div>
        )}

        {riskReport && (
          <div className="rovw mt-6">
            <RiskDomainsHeader />
            <SeverityLegend />
            <div className="rdom-pillar-grid">
              {pillars.map((pillar) => (
                <PillarCard
                  key={pillar.label}
                  pillar={pillar}
                  onOpen={() => setOpenPillarLabel(pillar.label)}
                />
              ))}
            </div>
            <DeploymentCharacteristics report={riskReport} />
            <ScopeNotes report={riskReport} />
          </div>
        )}
      </div>

      {openPillar && (
        <PillarDrawer pillar={openPillar} onClose={() => setOpenPillarLabel(null)} />
      )}
    </div>
  );
};

export default RiskPlusRiskDomains;
